import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useTheme } from '../context/ThemeContext';
import { usePlayer } from '../context/PlayerContext';
import { getThemeColors } from '../utils/themeStyles';
import {
  getMissionChatData,
  ChatMessage,
  DialogueChoice,
  DialogueNode,
  MissionChatData,
  getMissionChatScript,
  getTreeStepCount,
  isTreeMission,
} from '../data/mockChatData';
import { getScenarioChat } from '../data/scenarios/storyMapper';
import {
  useChatState,
  ChatItem,
  ChoiceTurnData,
  DisplayMsg,
  AnsweredQuestion,
  buildChatStateKey,
} from '../context/ChatStateContext';

const PLAYER_AVATARS: Record<number, any> = {
  1: require('../../assets/images/characters/egocrisis_raoul.jpg'),
};
const PLAYER_AVATAR_FALLBACK = require('../../assets/images/characters/egocrisis_raoul.jpg');

function getTimestamp(): string {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function checkAnswer(
  answer: string | string[],
  correctAnswer?: string | string[],
  type?: string,
): boolean {
  if (type === 'free-text') return true;
  if (!correctAnswer) return true;
  if (Array.isArray(correctAnswer)) {
    if (!Array.isArray(answer)) return false;
    return (
      JSON.stringify([...correctAnswer].sort()) ===
      JSON.stringify([...(answer as string[])].sort())
    );
  }
  return answer === correctAnswer;
}

/** Labels deja essayes pour un noeud / une question. */
function getAttemptedLabelsForQuestion(
  questionId: string,
  chatItems: ChatItem[],
  attemptedByNode: Record<string, string[]>,
): string[] {
  const labels = new Set<string>(attemptedByNode[questionId] ?? []);
  for (const item of chatItems) {
    if (item.kind === 'choice-turn' && item.data.nodeId === questionId) {
      item.data.attemptedLabels.forEach(a => labels.add(a));
      if (item.data.selectedLabel) labels.add(item.data.selectedLabel);
      continue;
    }
    if (item.kind !== 'answered' || item.data.question.id !== questionId) continue;
    item.data.attemptedAnswers?.forEach(a => labels.add(a));
    const ans = item.data.selectedAnswer;
    if (typeof ans === 'string') labels.add(ans);
    else if (Array.isArray(ans)) ans.forEach(a => labels.add(a));
  }
  return [...labels];
}

function findLastUnresolvedChoiceTurnIndex(items: ChatItem[], nodeId?: string): number {
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    if (item.kind !== 'choice-turn' || item.data.resolved) continue;
    if (nodeId !== undefined && item.data.nodeId !== nodeId) continue;
    return i;
  }
  return -1;
}

function createChoiceTurn(node: DialogueNode, patch?: Partial<ChoiceTurnData>): ChatItem {
  return {
    kind: 'choice-turn',
    data: {
      id: `ct-${node.id}-${Date.now()}`,
      nodeId: node.id,
      options: node.choices.map(c => ({ id: c.id, label: c.label })),
      attemptedLabels: [],
      pendingLabel: null,
      selectedLabel: null,
      resolved: false,
      ...patch,
    },
  };
}

/** Retire les tours de choix encore actifs pour un noeud (evite les doublons figes). */
function withoutActiveChoiceTurns(items: ChatItem[], nodeId: string): ChatItem[] {
  return items.filter(
    i => !(i.kind === 'choice-turn' && i.data.nodeId === nodeId && !i.data.resolved),
  );
}

/** Place le panneau de choix actif en bas du fil (apres les repliques). */
function normalizeChoiceTurnOrder(items: ChatItem[]): ChatItem[] {
  const idx = findLastUnresolvedChoiceTurnIndex(items);
  if (idx < 0 || idx === items.length - 1) return items;
  const turn = items[idx];
  const rest = items.filter((_, i) => i !== idx);
  return [...rest, turn];
}

/** Nettoie un etat sauvegarde (pending fige, panneau au milieu du fil). */
function sanitizeTreeChatItems(items: ChatItem[]): ChatItem[] {
  const cleared = items.map(item =>
    item.kind === 'choice-turn'
      ? { ...item, data: { ...item.data, pendingLabel: null } }
      : item,
  );
  return normalizeChoiceTurnOrder(cleared);
}

function resolveNodeForChoiceTurn(
  tree: { nodes: Record<string, DialogueNode> },
  data: ChoiceTurnData,
): DialogueNode | undefined {
  const byId = tree.nodes[data.nodeId];
  if (byId) return byId;
  // Backward compatibility for stale saved state: recover node from option labels.
  const labels = new Set(data.options.map(o => o.label));
  return Object.values(tree.nodes).find(n => n.choices.some(c => labels.has(c.label)));
}

function buildFeedback(isCorrect: boolean, q: ChatMessage): string {
  if (q.type === 'free-text') {
    return `Explication :\n${q.explanation ?? 'Merci pour votre reponse !'}`;
  }
  if (isCorrect) {
    return `Correct ! ${q.explanation ?? 'Bravo !'}`;
  }
  const correct = Array.isArray(q.correctAnswer)
    ? q.correctAnswer.join(', ')
    : (q.correctAnswer ?? '');
  return `Pas tout a fait. Bonne reponse : "${correct}"\n\n${q.explanation ?? ''}`;
}

function TypingDots({
  tutorAvatar,
  tutorColor,
  tutorImage,
  colors,
}: {
  tutorAvatar: string;
  tutorColor: string;
  tutorImage?: any;
  colors: ReturnType<typeof getThemeColors>;
}) {
  const [count, setCount] = useState(1);
  useEffect(() => {
    const id = setInterval(() => setCount(c => (c >= 3 ? 1 : c + 1)), 400);
    return () => clearInterval(id);
  }, []);
  return (
    <View style={td.row}>
      {tutorImage ? (
        <View style={td.avatarWrap}>
          <Image source={tutorImage} style={td.avatarImg} resizeMode="cover" />
        </View>
      ) : (
        <View style={[td.avatar, { backgroundColor: tutorColor }]}>
          <Text style={[td.avatarTxt, { color: '#FFFFFF' }]}>{tutorAvatar}</Text>
        </View>
      )}
      <View style={[td.bubble, { backgroundColor: colors.surfaceSecondary }]}>
        <Text style={[td.dots, { color: colors.textSecondary }]}>
          {'●  '.repeat(count).trimEnd()}
        </Text>
      </View>
    </View>
  );
}

const td = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, marginBottom: 8 },
  avatarWrap: {
    width: 32,
    height: 32,
    borderRadius: 16,
    overflow: 'hidden',
    flexShrink: 0,
  },
  avatarImg: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  avatarTxt: { fontSize: 10, fontWeight: 'bold' },
  bubble: {
    borderRadius: 16,
    borderBottomLeftRadius: 4,
    paddingHorizontal: 14,
    paddingVertical: 10,
    minWidth: 72,
  },
  dots: { fontSize: 18, letterSpacing: 4 },
});

export default function MissionChatScreen({ navigation: navProp, route: routeProp }: { navigation?: any; route?: any } = {}) {
  const navigation = navProp ?? useNavigation<any>();
  const route = routeProp ?? useRoute<any>();
  const { isDarkTheme } = useTheme();
  const { playerName, playerAvatar } = usePlayer();
  const colors = getThemeColors(isDarkTheme);
  const { width } = useWindowDimensions();
  const isWebDesktop = Platform.OS === 'web' && width >= 768;

  const scrollRef = useRef<ScrollView>(null);
  const chatStateStore = useChatState();

  const missionId: number | undefined = route.params?.missionId;
  const itemId: string | number | undefined = route.params?.itemId;

  // From chapter sequence → JSON chat; from global chat icon → legacy mock.
  const scenarioChat = missionId !== undefined ? getScenarioChat(missionId, itemId != null ? String(itemId) : undefined) : undefined;
  const missionData: MissionChatData | undefined = scenarioChat
    ? {
        missionId: scenarioChat.missionId,
        missionTitle: scenarioChat.missionTitle,
        tutorName: scenarioChat.tutorName,
        tutorAvatar: scenarioChat.tutorAvatar,
        tutorColor: scenarioChat.tutorColor,
        tutorImage: scenarioChat.tutorImage,
        tutorRole: scenarioChat.tutorRole,
        unreadCount: 0,
        lastActivity: '',
        messages: scenarioChat.messages as ChatMessage[],
      }
    : missionId !== undefined
      ? getMissionChatData(missionId)
      : undefined;
  const chatStateKey = missionId !== undefined ? buildChatStateKey(missionId, itemId) : null;
  const playerAvatarImg = playerAvatar ?? (missionId !== undefined ? (PLAYER_AVATARS[missionId] ?? PLAYER_AVATAR_FALLBACK) : PLAYER_AVATAR_FALLBACK);

  const [chatItems, setChatItems] = useState<ChatItem[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [activeQ, setActiveQ] = useState<ChatMessage | null>(null);
  const [selectedOpts, setSelectedOpts] = useState<string[]>([]);
  const [freeInput, setFreeInput] = useState('');
  const [answeredCount, setAnsweredCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [attemptedByNode, setAttemptedByNode] = useState<Record<string, string[]>>({});
  const [pendingChoiceLabel, setPendingChoiceLabel] = useState<string | null>(null);

  const scriptRef = useRef<ChatMessage[]>([]);
  const posRef = useRef(0);
  const currentNodeIdRef = useRef('');
  const isRunRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const choiceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isTree = missionData ? isTreeMission(missionData) : false;
  const conversationTree = missionData?.conversationTree;

  const totalSteps = missionData
    ? isTree && conversationTree
      ? getTreeStepCount(conversationTree)
      : getMissionChatScript(missionData).filter(m => m.type && m.type !== 'text').length
    : 0;

  const tutorColor = missionData?.tutorColor ?? colors.primary;

  const addItem = useCallback((item: ChatItem) => {
    setChatItems(prev => [...prev, item]);
  }, []);

  const patchChatItems = useCallback((patcher: (items: ChatItem[]) => ChatItem[]) => {
    setChatItems(prev => patcher(prev));
  }, []);

  const processNext = useCallback(() => {
    if (isRunRef.current) return;

    const pos = posRef.current;
    const script = scriptRef.current;

    if (pos >= script.length) {
      setIsComplete(true);
      return;
    }

    const item = script[pos];
    isRunRef.current = true;
    setIsTyping(true);

    timerRef.current = setTimeout(() => {
      isRunRef.current = false;
      setIsTyping(false);

      addItem({
        kind: 'msg',
        data: {
          id: `t-${pos}`,
          sender: 'tutor' as const,
          text: item.text,
          timestamp: getTimestamp(),
          characterImage: item.characterImage,
          characterName: item.characterName,
        },
      });

      posRef.current = pos + 1;

      if (!item.type || item.type === 'text') {
        processNext();
      } else {
        setActiveQ(item);
      }
    }, 900);
  }, [addItem]);

  const showNode = useCallback(
    (nodeId: string, opts?: { replayPrompt?: boolean }) => {
      if (!conversationTree) return;
      const node = conversationTree.nodes[nodeId];
      if (!node) {
        console.warn('[MissionChat] showNode missing node', nodeId);
        setIsComplete(true);
        return;
      }

      currentNodeIdRef.current = nodeId;
      console.log('[MissionChat] showNode', { nodeId, isEnd: Boolean(node.isEnd), replayPrompt: opts?.replayPrompt });

      if (node.isEnd) {
        isRunRef.current = true;
        setIsTyping(true);
        timerRef.current = setTimeout(() => {
          isRunRef.current = false;
          setIsTyping(false);
          addItem({
            kind: 'msg',
            data: {
              id: `t-end-${nodeId}`,
              sender: 'tutor',
              text: node.tutorText,
              timestamp: getTimestamp(),
            },
          });
          setIsComplete(true);
        }, 900);
        return;
      }

      const shouldShowPrompt = opts?.replayPrompt !== false;
      if (!shouldShowPrompt) return;

      isRunRef.current = true;
      setIsTyping(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        isRunRef.current = false;
        setIsTyping(false);
        setChatItems(prev => [
          ...prev,
          {
            kind: 'msg',
            data: {
              id: `t-node-${nodeId}-${Date.now()}`,
              sender: 'tutor',
              text: node.tutorText,
              timestamp: getTimestamp(),
            },
          },
          createChoiceTurn(node),
        ]);
      }, 900);
    },
    [addItem, conversationTree],
  );

  useEffect(() => {
    if (!missionData || missionId === undefined || !chatStateKey) return;

    const saved = chatStateStore.getState(chatStateKey);

    if (isTree && conversationTree) {
      if (saved && saved.chatItems.length > 0) {
        setChatItems(sanitizeTreeChatItems(saved.chatItems));
        setAnsweredCount(saved.answeredCount);
        setIsComplete(saved.isComplete);
        setIsTyping(false);
        setPendingChoiceLabel(null);
        if (saved.attemptedChoicesByNode) {
          setAttemptedByNode(saved.attemptedChoicesByNode);
        }
        const nodeId = saved.currentNodeId ?? conversationTree.startNodeId;
        currentNodeIdRef.current = nodeId;
        if (!saved.isComplete) {
          const node = conversationTree.nodes[nodeId];
          if (node && !node.isEnd && node.choices.length > 0) {
            const hasOpenTurn = saved.chatItems.some(
              i =>
                i.kind === 'choice-turn' &&
                i.data.nodeId === nodeId &&
                !i.data.resolved,
            );
            if (!hasOpenTurn) {
              setChatItems(prev => [...prev, createChoiceTurn(node)]);
            }
          }
        }
      } else {
        currentNodeIdRef.current = conversationTree.startNodeId;
        showNode(conversationTree.startNodeId);
      }
    } else {
      scriptRef.current = getMissionChatScript(missionData);

      if (saved && saved.chatItems.length > 0) {
        setChatItems(saved.chatItems);
        posRef.current = saved.pos;
        setAnsweredCount(saved.answeredCount);
        setIsComplete(saved.isComplete);
        if (!saved.isComplete) {
          processNext();
        }
      } else {
        posRef.current = 0;
        processNext();
      }
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (choiceTimerRef.current) clearTimeout(choiceTimerRef.current);
      isRunRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (missionId === undefined || !chatStateKey) return;
    chatStateStore.setState(chatStateKey, {
      chatItems,
      pos: posRef.current,
      answeredCount,
      isComplete,
      currentNodeId: isTree ? currentNodeIdRef.current : undefined,
      attemptedChoicesByNode: attemptedByNode,
    });
  }, [chatItems, answeredCount, isComplete, missionId, chatStateKey, chatStateStore, isTree, attemptedByNode]);

  useEffect(() => {
    const t = setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 80);
    return () => clearTimeout(t);
  }, [chatItems, isTyping, activeQ, pendingChoiceLabel]);

  const handleTreeChoice = useCallback(
    (choice: DialogueChoice, node: DialogueNode) => {
      if (!conversationTree) return;
      if (isTyping) {
        const isActuallyRunning = Boolean(timerRef.current || choiceTimerRef.current);
        if (isActuallyRunning) {
          console.log('[MissionChat] Ignored click while typing', { nodeId: node.id, choiceId: choice.id });
          return;
        }
        console.warn('[MissionChat] Recovered from stale typing lock');
        setIsTyping(false);
      }

      console.log('[MissionChat] handleTreeChoice', {
        nodeId: node.id,
        choiceId: choice.id,
        advances: choice.advances,
        nextNodeId: choice.nextNodeId,
      });

      const nodeId = node.id;
      const attemptedSnapshot = [
        ...new Set([...(attemptedByNode[nodeId] ?? []), choice.label]),
      ];
      setAttemptedByNode(prev => ({ ...prev, [nodeId]: attemptedSnapshot }));

      const feedbackMsg: ChatItem = {
        kind: 'msg',
        data: {
          id: `f-${choice.id}-${Date.now()}`,
          sender: 'tutor',
          text: choice.tutorResponse,
          timestamp: getTimestamp(),
          isCorrect: node.isAssessment ? choice.advances : undefined,
        },
      };

      patchChatItems(prev => {
        const next = withoutActiveChoiceTurns(prev, nodeId);
        next.push(
          createChoiceTurn(node, {
            attemptedLabels: attemptedSnapshot,
            selectedLabel: choice.label,
            resolved: true,
          }),
        );
        next.push(feedbackMsg);
        if (!choice.advances) {
          const nextNode = conversationTree.nodes[choice.nextNodeId] ?? node;
          next.push(
            createChoiceTurn(nextNode, {
              attemptedLabels: nextNode.id === nodeId ? attemptedSnapshot : [],
            }),
          );
        }
        return next;
      });

      currentNodeIdRef.current = choice.nextNodeId;
      setPendingChoiceLabel(null);

      if (!choice.advances) {
        return;
      }

      isRunRef.current = true;
      setIsTyping(true);
      if (choiceTimerRef.current) clearTimeout(choiceTimerRef.current);
      choiceTimerRef.current = setTimeout(() => {
        choiceTimerRef.current = null;
        isRunRef.current = false;
        setIsTyping(false);
        setPendingChoiceLabel(null);
        setAnsweredCount(c => c + 1);
        setAttemptedByNode(prev => {
          const next = { ...prev };
          delete next[nodeId];
          return next;
        });
        console.log('[MissionChat] advancing to node', choice.nextNodeId);
        showNode(choice.nextNodeId);
      }, 700);
    },
    [patchChatItems, showNode, conversationTree, attemptedByNode, isTyping],
  );

  const handleAnswer = useCallback(
    (answer: string | string[]) => {
      if (!activeQ) return;
      const q = activeQ;

      if (q.type !== 'free-text') {
        const attemptedAnswers = Array.isArray(answer)
          ? answer
          : [answer as string];
        addItem({
          kind: 'answered',
          data: {
            id: `aq-${Date.now()}`,
            question: q,
            selectedAnswer: answer,
            attemptedAnswers,
            timestamp: getTimestamp(),
          },
        });
      }

      if (q.type === 'free-text') {
        const ansText = Array.isArray(answer) ? answer.join(', ') : answer;
        addItem({
          kind: 'msg',
          data: {
            id: `u-${Date.now()}`,
            sender: 'user' as const,
            text: ansText,
            timestamp: getTimestamp(),
          },
        });
      }

      setActiveQ(null);
      setSelectedOpts([]);
      setFreeInput('');
      setAnsweredCount(c => c + 1);

      const isCorrect = checkAnswer(answer, q.correctAnswer, q.type);

      setIsTyping(true);
      timerRef.current = setTimeout(() => {
        setIsTyping(false);
        addItem({
          kind: 'msg',
          data: {
            id: `f-${Date.now()}`,
            sender: 'tutor' as const,
            text: buildFeedback(isCorrect, q),
            timestamp: getTimestamp(),
            isCorrect,
            characterImage: q.characterImage,
            characterName: q.characterName,
          },
        });
        processNext();
      }, 900);
    },
    [activeQ, addItem, processNext],
  );

  const toggleOpt = (opt: string) =>
    setSelectedOpts(prev => (prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt]));

  const handleNextItem = () => {
    const nextType = route.params?.nextItemType;
    const nextId = route.params?.nextItemId;
    const nextTitle = route.params?.nextItemTitle;
    const nextSceneNumber = route.params?.nextItemSceneNumber;
    const params = { missionId, chapterId: route.params?.chapterId, itemId: nextId, title: nextTitle };
    switch (nextType) {
      case 'scene':
        navigation.navigate('SceneReader' as never, { ...params, sceneNumber: nextSceneNumber } as never);
        break;
      case 'chat':
        navigation.navigate('MissionChat' as never, params as never);
        break;
      case 'card':
        navigation.navigate('MissionCards' as never, params as never);
        break;
      case 'quiz':
        navigation.navigate('Quiz' as never, params as never);
        break;
      case 'task':
        navigation.navigate('TaskDetail' as never, params as never);
        break;
    }
  };

  const renderMessage = (msg: DisplayMsg) => {
    const isUser = msg.sender === 'user';
    const hasFeedback = msg.isCorrect !== undefined;
    const feedbackBg = msg.isCorrect === true
      ? (isDarkTheme ? '#064E3B' : '#D1FAE5')
      : msg.isCorrect === false
        ? (isDarkTheme ? '#7F1D1D' : '#FEE2E2')
        : undefined;
    const feedbackBorder = msg.isCorrect === true ? '#10B981' : msg.isCorrect === false ? '#EF4444' : undefined;

    return (
      <View
        key={msg.id}
        style={[styles.msgRow, isUser ? styles.msgRowRight : styles.msgRowLeft]}
      >
        {!isUser && (
          (msg.characterImage ?? missionData?.tutorImage) ? (
            <View style={styles.msgAvatarWrap}>
              <Image
                source={msg.characterImage ?? missionData?.tutorImage}
                style={styles.msgAvatarImage}
                resizeMode="cover"
              />
            </View>
          ) : (
            <View style={[styles.msgAvatar, { backgroundColor: tutorColor }]}>
              <Text style={[styles.msgAvatarText, { color: '#FFFFFF' }]}>
                {missionData?.tutorAvatar ?? '?'}
              </Text>
            </View>
          )
        )}
        <View style={[styles.msgContent, isUser ? styles.msgContentRight : styles.msgContentLeft]}>
          {!isUser && (
            <Text style={[styles.senderName, { color: colors.textSecondary }]}>
              {msg.characterName ?? missionData?.tutorName}
            </Text>
          )}
          <View
            style={[
              styles.bubble,
              isUser
                ? [styles.bubbleMe, { backgroundColor: colors.primary }]
                : hasFeedback && feedbackBg
                  ? [styles.bubbleTutor, { backgroundColor: feedbackBg, borderWidth: 1, borderColor: feedbackBorder }]
                  : [styles.bubbleTutor, { backgroundColor: colors.surfaceSecondary }],
            ]}
          >
            <Text
              style={[
                styles.bubbleText,
                { color: isUser ? colors.badgeText : colors.textPrimary },
              ]}
            >
              {msg.text}
            </Text>
          </View>
          <Text
            style={[
              styles.timestamp,
              { color: colors.textTertiary },
              isUser && styles.timestampRight,
            ]}
          >
            {msg.timestamp}
          </Text>
        </View>
        {isUser && (
          <View style={styles.msgAvatarWrap}>
            <Image source={playerAvatarImg ?? PLAYER_AVATAR_FALLBACK} style={styles.msgAvatarImage} resizeMode="cover" />
          </View>
        )}
      </View>
    );
  };

  const wrapUserChoiceArea = (key: string, children: React.ReactNode) => (
    <View key={key} style={[styles.msgRow, styles.msgRowRight]}>
      <View style={[styles.msgContent, styles.msgContentRight, styles.choiceColumn]}>
        {children}
      </View>
      <View style={styles.msgAvatarWrap}>
        <Image
          source={playerAvatarImg ?? PLAYER_AVATAR_FALLBACK}
          style={styles.msgAvatarImage}
          resizeMode="cover"
        />
      </View>
    </View>
  );

  const renderChoicesPanel = (
    questionId: string,
    options: { id: string; label: string }[],
    config: {
      hint: string;
      readOnly?: boolean;
      selectedLabel?: string | null;
      selectedLabels?: string[];
      attemptedOverride?: string[];
      pendingLabel?: string | null;
      onSelect?: (label: string) => void;
    },
  ) => {
    const attempted =
      config.attemptedOverride ??
      getAttemptedLabelsForQuestion(questionId, chatItems, attemptedByNode);
    const readOnly = config.readOnly ?? false;
    const selectedLabel = config.selectedLabel ?? null;
    const selectedLabels = config.selectedLabels ?? [];
    const pendingLabel = config.pendingLabel ?? null;
    const effectivePending =
      !readOnly && isTyping && pendingLabel ? pendingLabel : null;

    return (
      <View style={styles.inlineOptions} pointerEvents={readOnly ? 'none' : 'auto'}>
        <Text style={[styles.optHint, { color: colors.textSecondary }]}>{config.hint}</Text>
        {options.map(opt => {
          const isPending = effectivePending === opt.label;
          const isSelected =
            isPending ||
            selectedLabel === opt.label ||
            selectedLabels.includes(opt.label);
          const isTried = attempted.includes(opt.label) && !isPending;
          const isDisabled =
            (isTyping && !isPending) || (isTried && !isSelected) || (readOnly && !isSelected);

          if (isSelected && (readOnly || isPending)) {
            return (
              <View
                key={opt.id}
                style={[
                  styles.optBtn,
                  styles.optBtnFullWidth,
                  styles.optBtnLocked,
                  { borderColor: colors.primary, backgroundColor: colors.primary },
                ]}
              >
                <View style={styles.checkRow}>
                  <Ionicons name="checkmark" size={14} color={colors.badgeText} style={styles.checkIcon} />
                  <Text style={[styles.optBtnText, { color: colors.badgeText }]}>{opt.label}</Text>
                </View>
              </View>
            );
          }

          if (isSelected && !readOnly && !isPending && config.onSelect) {
            return (
              <TouchableOpacity
                key={opt.id}
                style={[
                  styles.optBtn,
                  styles.optBtnFullWidth,
                  {
                    borderColor: colors.primary,
                    backgroundColor: isDarkTheme ? '#1E3A8A' : '#EFF6FF',
                  },
                ]}
                onPress={() => config.onSelect?.(opt.label)}
                activeOpacity={0.7}
              >
                <View style={styles.checkRow}>
                  <View
                    style={[
                      styles.checkbox,
                      {
                        borderColor: colors.primary,
                        backgroundColor: colors.primary,
                      },
                    ]}
                  >
                    <Ionicons name="checkmark" size={11} color={colors.badgeText} />
                  </View>
                  <Text style={[styles.optBtnText, { color: colors.textPrimary }]}>{opt.label}</Text>
                </View>
              </TouchableOpacity>
            );
          }

          if (isDisabled) {
            return (
              <View
                key={opt.id}
                style={[
                  styles.optBtn,
                  styles.optBtnFullWidth,
                  styles.optBtnDisabled,
                  {
                    borderColor: colors.border,
                    backgroundColor: colors.surfaceSecondary,
                  },
                ]}
              >
                <Text style={[styles.optBtnText, { color: colors.textTertiary }]}>{opt.label}</Text>
              </View>
            );
          }

          return (
            <TouchableOpacity
              key={opt.id}
              style={[
                styles.optBtn,
                styles.optBtnFullWidth,
                { borderColor: colors.primary, backgroundColor: colors.surfaceSecondary },
              ]}
              onPress={() => config.onSelect?.(opt.label)}
              activeOpacity={0.7}
            >
              <Text style={[styles.optBtnText, { color: colors.textPrimary }]}>{opt.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  };

  const renderAnsweredQuestion = (item: AnsweredQuestion) => {
    const { question: q, selectedAnswer } = item;

    if ((q.type === 'single-choice' || q.type === 'dialogue-choice') && q.options) {
      const chosen = typeof selectedAnswer === 'string' ? selectedAnswer : '';
      if (!chosen) return null;
      return wrapUserChoiceArea(
        item.id,
        renderChoicesPanel(
          q.id,
          q.options.map((label, i) => ({ id: `${item.id}-opt-${i}`, label })),
          {
            hint: 'Votre reponse :',
            readOnly: true,
            selectedLabel: chosen,
            attemptedOverride: item.attemptedAnswers,
          },
        ),
      );
    }

    if (q.type === 'multiple-choice' && q.options) {
      const chosen: string[] = Array.isArray(selectedAnswer) ? selectedAnswer : [];
      if (chosen.length === 0) return null;
      return wrapUserChoiceArea(
        item.id,
        renderChoicesPanel(
          q.id,
          q.options.map((label, i) => ({ id: `${item.id}-opt-${i}`, label })),
          {
            hint: 'Vos reponses :',
            readOnly: true,
            selectedLabels: chosen,
            attemptedOverride: item.attemptedAnswers ?? chosen,
          },
        ),
      );
    }

    return null;
  };

  const renderChoiceTurn = (data: ChoiceTurnData) => {
    const node = conversationTree ? resolveNodeForChoiceTurn(conversationTree, data) : undefined;
    return wrapUserChoiceArea(
      data.id,
      renderChoicesPanel(data.nodeId, data.options, {
        hint: data.resolved
          ? 'Votre reponse :'
          : 'Choisissez une reponse pour poursuivre la discussion :',
        readOnly: data.resolved,
        selectedLabel: data.selectedLabel,
        attemptedOverride: data.attemptedLabels,
        pendingLabel: data.resolved ? null : pendingChoiceLabel,
        onSelect:
          !data.resolved && node
            ? label => {
                const choice = node.choices.find(c => c.label === label);
              if (choice) {
                handleTreeChoice(choice, node);
                return;
              }
              console.warn('[MissionChat] No choice matched label', {
                nodeId: node.id,
                label,
              });
              }
            : undefined,
      }),
    );
  };

  const renderActiveQuestion = () => {
    if (isTree) return null;
    if (!activeQ) return null;

    if (activeQ.type === 'single-choice' && activeQ.options) {
      return wrapUserChoiceArea(
        `active-q-${activeQ.id}`,
        renderChoicesPanel(
          activeQ.id,
          activeQ.options.map((label, i) => ({ id: `${activeQ.id}-opt-${i}`, label })),
          {
            hint: 'Choisissez une reponse :',
            onSelect: label => handleAnswer(label),
          },
        ),
      );
    }

    if (activeQ.type === 'multiple-choice' && activeQ.options) {
      return wrapUserChoiceArea(
        `active-q-${activeQ.id}`,
        <View style={styles.choiceColumn}>
          {renderChoicesPanel(
            activeQ.id,
            activeQ.options.map((label, i) => ({ id: `${activeQ.id}-opt-${i}`, label })),
            {
              hint: 'Selectionnez une ou plusieurs reponses :',
              selectedLabels: selectedOpts,
              onSelect: label => toggleOpt(label),
            },
          )}
          <TouchableOpacity
            style={[
              styles.validateBtn,
              styles.optBtnFullWidth,
              { backgroundColor: selectedOpts.length > 0 ? colors.primary : colors.surfaceSecondary },
            ]}
            onPress={() => { if (selectedOpts.length > 0) handleAnswer(selectedOpts); }}
            disabled={selectedOpts.length === 0 || isTyping}
          >
            <Text
              style={[
                styles.validateBtnText,
                { color: selectedOpts.length > 0 ? colors.badgeText : colors.textTertiary },
              ]}
            >
              {selectedOpts.length > 0 ? `Valider (${selectedOpts.length} selectionne${selectedOpts.length > 1 ? 's' : ''})` : 'Valider'}
            </Text>
          </TouchableOpacity>
        </View>,
      );
    }

    if (activeQ.type === 'free-text') {
      return wrapUserChoiceArea(
        `active-q-${activeQ.id}`,
        <View style={styles.inlineOptions}>
          <Text style={[styles.optHint, { color: colors.textSecondary }]}>
            Repondez en texte libre :
          </Text>
          <View style={styles.freeRow}>
            <TextInput
              style={[
                styles.freeInput,
                {
                  backgroundColor: colors.inputBackground,
                  color: colors.inputText,
                  borderColor: colors.inputBorder,
                },
              ]}
              placeholder="Votre reponse..."
              placeholderTextColor={colors.inputPlaceholder}
              value={freeInput}
              onChangeText={setFreeInput}
              multiline
              maxLength={500}
            />
            <TouchableOpacity
              style={[
                styles.sendBtn,
                { backgroundColor: freeInput.trim() ? colors.primary : colors.surfaceSecondary },
              ]}
              onPress={() => { if (freeInput.trim()) handleAnswer(freeInput.trim()); }}
              disabled={!freeInput.trim()}
            >
              <Ionicons
                name="send"
                size={20}
                color={freeInput.trim() ? colors.badgeText : colors.textTertiary}
              />
            </TouchableOpacity>
          </View>
        </View>,
      );
    }

    return null;
  };

  if (!missionId || !missionData) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>Chat Mission</Text>
          <View style={{ width: 32 }} />
        </View>
        <View style={styles.emptyState}>
          <Ionicons name="chatbox-outline" size={64} color={colors.textTertiary} />
          <Text style={[styles.emptyTitle, { color: colors.textPrimary }]}>Mission introuvable</Text>
          <Text style={[styles.emptySubtitle, { color: colors.textSecondary }]}>
            Impossible de charger le chat pour cette mission.
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[
        styles.container,
        { backgroundColor: isWebDesktop ? colors.surfaceSecondary : colors.background },
      ]}
    >
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
        </TouchableOpacity>
        {missionData.tutorImage ? (
          <View style={styles.headerAvatarWrap}>
            <Image source={missionData.tutorImage} style={styles.headerAvatarImage} resizeMode="cover" />
          </View>
        ) : (
          <View style={[styles.headerAvatar, { backgroundColor: tutorColor }]}>
            <Text style={[styles.headerAvatarText, { color: '#FFFFFF' }]}>
              {missionData.tutorAvatar}
            </Text>
          </View>
        )}
        <View style={styles.headerInfo}>
          <Text style={[styles.headerTitle, { color: colors.textPrimary }]} numberOfLines={1}>
            {missionData.tutorName}
          </Text>
          <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]} numberOfLines={1}>
            {missionData.missionTitle}
          </Text>
        </View>
        <View style={[styles.progressBadge, { backgroundColor: colors.accent }]}>
          <Ionicons name="checkmark-circle-outline" size={14} color={colors.primary} />
          <Text style={[styles.progressText, { color: colors.primary }]}>
            {answeredCount}/{totalSteps}
          </Text>
        </View>
      </View>

      <View style={[styles.outer, isWebDesktop && styles.outerDesktop]}>
        <KeyboardAvoidingView
          style={[
            styles.chatBox,
            isWebDesktop && styles.chatBoxDesktop,
            { backgroundColor: colors.background },
          ]}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          keyboardVerticalOffset={90}
        >
          <ScrollView
            ref={scrollRef}
            style={styles.msgList}
            contentContainerStyle={styles.msgListContent}
            showsVerticalScrollIndicator={false}
          >
            {chatItems.map(item => {
              if (item.kind === 'msg') return renderMessage(item.data);
              if (item.kind === 'choice-turn') return renderChoiceTurn(item.data);
              return renderAnsweredQuestion(item.data);
            })}
            {isTyping && (
              <TypingDots
                tutorAvatar={missionData.tutorAvatar}
                tutorColor={tutorColor}
                tutorImage={missionData.tutorImage}
                colors={colors}
              />
            )}
            {renderActiveQuestion()}
            {isComplete && !isTyping && (
              <View
                style={[
                  styles.completionBanner,
                  { backgroundColor: colors.accent, borderColor: colors.primary },
                ]}
              >
                <Ionicons name="trophy-outline" size={22} color={colors.primary} />
                <Text style={[styles.completionText, { color: colors.primary }]}>
                  Session terminee — {answeredCount}/{totalSteps}{' '}
                  {isTree ? 'echanges' : 'questions repondues'}
                </Text>
              </View>
            )}
            {isComplete && !isTyping && (
              <View style={styles.actionRow}>
                <TouchableOpacity
                  style={[styles.closePill, { borderColor: colors.border }]}
                  onPress={() => navigation.goBack()}
                >
                  <Text style={[styles.closePillText, { color: colors.textSecondary }]}>{'✕'} Fermer</Text>
                </TouchableOpacity>
                {route.params?.nextItemType && !route.params?.isLastItem && (
                  <TouchableOpacity
                    style={[styles.nextPill, { backgroundColor: colors.primary }]}
                    onPress={() => handleNextItem()}
                  >
                    <Text style={[styles.nextPillText, { color: colors.badgeText }]}>Suivant →</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    gap: 10,
  },
  backBtn: { padding: 4 },
  headerAvatarWrap: { width: 38, height: 38, borderRadius: 19, overflow: 'hidden', flexShrink: 0 },
  headerAvatarImage: { width: 38, height: 38, borderRadius: 19 },
  headerAvatar: { width: 38, height: 38, borderRadius: 19, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  headerAvatarText: { fontSize: 13, fontWeight: 'bold' },
  headerInfo: { flex: 1 },
  headerTitle: { fontSize: 15, fontWeight: '700' },
  headerSubtitle: { fontSize: 12, marginTop: 1 },
  progressBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 12 },
  progressText: { fontSize: 13, fontWeight: '700' },
  outer: { flex: 1 },
  outerDesktop: { alignItems: 'center', paddingHorizontal: 24, paddingVertical: 16 },
  chatBox: { flex: 1 },
  chatBoxDesktop: { width: '100%' as any, maxWidth: 800, borderRadius: 16, overflow: 'hidden', shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.1, shadowRadius: 20, elevation: 8 },
  msgList: { flex: 1 },
  msgListContent: { padding: 16, paddingBottom: 24 },
  msgRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, marginBottom: 12 },
  msgRowLeft: { justifyContent: 'flex-start' },
  msgRowRight: { justifyContent: 'flex-end' },
  msgAvatarWrap: { width: 32, height: 32, borderRadius: 16, overflow: 'hidden', flexShrink: 0 },
  msgAvatarImage: { width: 32, height: 32, borderRadius: 16 },
  msgAvatar: { width: 32, height: 32, borderRadius: 16, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  msgAvatarText: { fontSize: 11, fontWeight: 'bold' },
  msgContent: { maxWidth: '76%' },
  msgContentLeft: { alignItems: 'flex-start' },
  msgContentRight: { alignItems: 'flex-end' },
  choiceColumn: { alignSelf: 'flex-end', width: '100%' },
  senderName: { fontSize: 11, fontWeight: '600', marginBottom: 3, marginLeft: 4 },
  bubble: { borderRadius: 16, paddingHorizontal: 14, paddingVertical: 10 },
  bubbleMe: { borderBottomRightRadius: 4 },
  bubbleTutor: { borderBottomLeftRadius: 4 },
  bubbleText: { fontSize: 14, lineHeight: 20 },
  timestamp: { fontSize: 10, marginTop: 3, marginHorizontal: 4 },
  timestampRight: { alignSelf: 'flex-end' },
  inlineOptions: { marginBottom: 4, gap: 8, width: '100%' },
  optHint: { fontSize: 12, fontWeight: '500', marginBottom: 2, textAlign: 'right' },
  optBtn: { borderWidth: 1.5, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 11 },
  optBtnFullWidth: { alignSelf: 'stretch' },
  optBtnLocked: { opacity: 0.92 },
  optBtnDisabled: { opacity: 0.55 },
  optBtnText: { fontSize: 13, fontWeight: '500', lineHeight: 18 },
  checkRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  checkIcon: { marginRight: 2 },
  checkbox: { width: 18, height: 18, borderRadius: 4, borderWidth: 2, justifyContent: 'center', alignItems: 'center' },
  validateBtn: { borderRadius: 10, paddingVertical: 12, alignItems: 'center', marginTop: 4 },
  validateBtnText: { fontSize: 14, fontWeight: '600' },
  freeRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 10 },
  freeInput: { flex: 1, borderWidth: 1, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 10, maxHeight: 100, fontSize: 14 },
  sendBtn: { width: 44, height: 44, borderRadius: 22, justifyContent: 'center', alignItems: 'center' },
  completionBanner: { flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderRadius: 12, padding: 14, marginTop: 8, marginBottom: 16 },
  completionText: { fontSize: 14, fontWeight: '600', flex: 1 },
  actionRow: { flexDirection: 'row', justifyContent: 'flex-end', paddingVertical: 12, paddingHorizontal: 4, gap: 8, marginTop: 8 },
  closePill: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, borderWidth: 1, backgroundColor: 'transparent' },
  closePillText: { fontSize: 14, fontWeight: '600' },
  nextPill: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20 },
  nextPillText: { fontSize: 14, fontWeight: '700' },
  emptyState: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 },
  emptyTitle: { fontSize: 18, fontWeight: '700', marginTop: 16, marginBottom: 8 },
  emptySubtitle: { fontSize: 14, textAlign: 'center' },
});

